import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDashboard, type Trend } from "@/api/dashboard";
import { useHousehold } from "@/app/useHousehold";
import { Amount } from "@/components/Amount";
import { CashFlowChart } from "@/components/CashFlowChart";
import { CategoryBars } from "@/components/CategoryBars";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import { EmptyState, ErrorNote, Loading, PageTitle, Section, rowsClass } from "@/components/ui";

/**
 * The dashboard (`project.md` §5.3).
 *
 * **It answers exactly one question: am I okay this month?** One number, large,
 * with a direction of travel. Everything below it is secondary and is laid out
 * to look secondary — a screen where the answer competes with four panels of
 * equal weight has not answered anything.
 *
 * The comparison behind the arrow is the part worth knowing about. It is *not*
 * this month against last month's total: on the 3rd a household has usually been
 * paid and has not yet paid its rent, and against a completed month that reads
 * as a windfall — then as a catastrophe eight days later. The server compares
 * against the same *day number* of the previous month, because the things that
 * dominate a household's month land on day numbers rather than after a count of
 * elapsed days. Where last month has nothing in it, no arrow is drawn at all.
 *
 * The charts follow ADR 0003, which is where the `dataviz` skill `project.md`
 * §5.6 names would have been.
 */
export function DashboardPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const household = useHousehold();
  const currency = household?.base_currency_code ?? "EUR";

  // `?as_of=` pins the screen to a day. The server has taken this parameter
  // since the endpoint existed — a dashboard whose answer cannot be tied to a
  // date cannot be tested, and K-40's demo walkthrough will want to open the
  // month its seeded data is in. Absent, it means today.
  const [params] = useSearchParams();
  const asOf = params.get("as_of") ?? undefined;

  const dashboard = useDashboard(householdId, asOf);
  const data = dashboard.data;

  return (
    <div>
      <PageTitle>{t("nav.dashboard")}</PageTitle>

      {dashboard.isLoading && <Loading />}
      <ErrorNote error={dashboard.error} />

      {data && (
        <>
          {/*
            The answer. This is the one bold moment the design allows itself —
            everything below is deliberately quieter than this. The double rule
            underneath is the bookkeeper's mark for "this is the final total",
            used here because the figure above it genuinely is one: what is left
            once this month's money in and out are combined.
          */}
          <div className="mt-4">
            <p className="text-sm text-muted">{t("dashboard.leftThisMonth")}</p>
            {/* The rule is as wide as the figure it closes, as it would be in
                a ledger — not a line across the page. */}
            <div className="mt-1 inline-block">
              <p className="figure text-figure" data-testid="month-net">
                {format.amount(data.trend.current.net_minor, currency)}
              </p>
              <div className="rule-total mt-2" />
            </div>

            <DirectionOfTravel trend={data.trend} currency={currency} />

            <p className="mt-2 text-sm text-muted" data-testid="month-flow">
              <Amount
                minor={data.trend.current.in_minor}
                currency={currency}
                testId="month-in"
              />{" "}
              {t("dashboard.received")} ·{" "}
              <Amount
                minor={-data.trend.current.spent_minor}
                currency={currency}
                testId="month-out"
              />{" "}
              {t("dashboard.spent")}
            </p>
          </div>

          {data.trend.current.lines === 0 && (
            <EmptyState title={t("dashboard.emptyMonth")}>
              <Link
                to={`/households/${householdId}/transactions/new`}
                className="text-accent-text hover:underline"
              >
                {t("dashboard.recordFirst")}
              </Link>
            </EmptyState>
          )}

          <Section title={t("dashboard.inAndOut")}>
            <CashFlowChart history={data.history} currency={currency} />
          </Section>

          <Section title={t("dashboard.whereItWent")}>
            {data.top_categories.length === 0 ? (
              <p className="text-sm text-muted">{t("dashboard.nothingCategorised")}</p>
            ) : (
              <CategoryBars categories={data.top_categories} currency={currency} />
            )}
          </Section>

          <Section title={t("dashboard.netPosition")}>
            {data.net_position.unavailable ? (
              // Declining is the honest answer: a net-worth figure quietly
              // missing an account looks exactly like a correct one.
              <p className="text-sm text-muted" data-testid="net-position-unavailable">
                {t("dashboard.netPositionUnavailable", {
                  pair: data.net_position.missing_pair ?? "",
                })}
              </p>
            ) : (
              <>
                <p className="num text-2xl font-semibold text-ink" data-testid="net-position">
                  {format.amount(data.net_position.total_minor, currency)}
                </p>
                <p className="mt-1 text-xs text-muted">
                  {t(
                    data.net_position.accounts === 1
                      ? "dashboard.acrossOneAccount"
                      : "dashboard.acrossAccounts",
                    { n: data.net_position.accounts },
                  )}
                  {data.net_position.converted > 0 && ` · ${t("dashboard.withConversion")}`}
                </p>
              </>
            )}
          </Section>

          <Section
            title={t("dashboard.recent")}
            action={
              <Link
                to={`/households/${householdId}/transactions`}
                className="text-xs text-accent-text hover:underline"
              >
                {t("dashboard.allTransactions")}
              </Link>
            }
          >
            {data.recent.length === 0 ? (
              <p className="text-sm text-muted">{t("dashboard.noneYet")}</p>
            ) : (
              <ul className={rowsClass}>
                {data.recent.map((booking) => (
                  <li
                    key={booking.id}
                    className="flex items-center justify-between gap-4 py-2.5 text-sm"
                    data-testid="recent-booking"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-ink">
                        {booking.payee_raw || t("dashboard.noPayee")}
                      </span>
                      <span className="text-xs text-muted">
                        {format.date(booking.booked_date)}
                      </span>
                    </span>
                    <Amount
                      minor={booking.money.amount_minor}
                      currency={booking.money.currency_code}
                      testId="recent-amount"
                    />
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/*
            The way to the history (K-37). It sits at the foot of the overview
            rather than in the tab bar because "who changed this" is a question
            somebody asks while looking at a number, not one they set out to
            navigate to — and because eight tabs is what the speed gate has
            already been measured against.
          */}
          <p className="mt-8 text-xs text-muted">
            <Link
              to={`/households/${householdId}/history`}
              className="text-accent-text hover:underline"
              data-testid="history-link"
            >
              {t("dashboard.whoChangedWhat")}
            </Link>
            {" · "}
            <Link
              to={`/households/${householdId}/data`}
              className="text-accent-text hover:underline"
              data-testid="data-link"
            >
              {t("dashboard.yourData")}
            </Link>
          </p>
        </>
      )}
    </div>
  );
}

/**
 * The arrow, and the sentence that makes it mean something.
 *
 * Two things it deliberately does not do. It is **not red when the month is
 * negative** — §5.5 reserves red for overspend, and a month that ends slightly
 * down is normal rather than alarming. And it says **nothing at all** when last
 * month has nothing to compare with, rather than showing a triumphant arrow
 * against a month nobody has entered yet.
 */
function DirectionOfTravel({ trend, currency }: { trend: Trend; currency: string }) {
  const t = useT();
  const format = useFormat();
  if (!trend.is_meaningful) {
    return (
      <p className="mt-1 text-sm text-muted" data-testid="trend">
        {t("dashboard.noComparison")}
      </p>
    );
  }

  const arrow = trend.direction > 0 ? "↑" : trend.direction < 0 ? "↓" : "→";
  const word =
    trend.direction > 0
      ? t("dashboard.more")
      : trend.direction < 0
        ? t("dashboard.less")
        : t("dashboard.same");
  const through = format.date(trend.previous.through);

  return (
    <p className="mt-1 text-sm text-muted" data-testid="trend">
      {/* The arrow is decoration: the sentence beside it carries the meaning,
          so a reader who cannot see the glyph loses nothing (ADR 0003). */}
      <span aria-hidden="true" className="mr-1">
        {arrow}
      </span>
      <span data-testid="trend-delta">
        {format.amount(Math.abs(trend.delta_minor), currency)} {word}
      </span>{" "}
      {t("dashboard.thanByLastMonth", { date: through })}
    </p>
  );
}
